import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { memberService } from '../../services/memberService';
import { classService } from '../../services/classService';
import { reservationService } from '../../services/reservationService';
import { Loader } from '../../components/Loader';

const Container = styled.div``;

const Title = styled.h1`
  color: ${colors.primary};
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const Card = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const CardTitle = styled.h3`
  color: ${colors.textLight};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 10px;
`;

const CardValue = styled.div`
  color: ${colors.primary};
  font-size: 36px;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Section = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
  margin-bottom: 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const SectionTitle = styled.h2`
  color: ${colors.primary};
  margin-bottom: 20px;
  font-size: 20px;
`;

const Alert = styled.div`
  background-color: ${props => {
    if (props.type === 'warning') return '#fff3cd';
    if (props.type === 'danger') return '#f8d7da';
    return '#d1ecf1';
  }};
  color: ${props => {
    if (props.type === 'warning') return '#856404';
    if (props.type === 'danger') return '#721c24';
    return '#0c5460';
  }};
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 15px;
  border-left: 4px solid ${props => {
    if (props.type === 'warning') return '#ffc107';
    if (props.type === 'danger') return '#dc3545';
    return '#17a2b8';
  }};
`;

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalClasses: 0,
    pendingReservations: 0
  });
  const [expiringMembers, setExpiringMembers] = useState([]);
  const [lowCreditMembers, setLowCreditMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use optimized dashboard stats endpoint
      const dashboardData = await memberService.getDashboardStats();
      
      setStats(dashboardData.stats);
      setExpiringMembers(dashboardData.expiringMembers);
      setLowCreditMembers(dashboardData.lowCreditMembers);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Title>Dashboard</Title>

      <Grid>
        <Card>
          <CardTitle>Toplam Üye</CardTitle>
          <CardValue>{stats.totalMembers}</CardValue>
        </Card>
        <Card>
          <CardTitle>Aktif Üye</CardTitle>
          <CardValue>{stats.activeMembers}</CardValue>
        </Card>
        <Card>
          <CardTitle>Toplam Ders</CardTitle>
          <CardValue>{stats.totalClasses}</CardValue>
        </Card>
        <Card>
          <CardTitle>Bekleyen Rezervasyon</CardTitle>
          <CardValue>{stats.pendingReservations}</CardValue>
        </Card>
      </Grid>

      {expiringMembers.length > 0 && (
        <Section>
          <SectionTitle>⚠️ Üyeliği Yakında Bitecek Üyeler</SectionTitle>
          {expiringMembers.map(member => (
            <Alert type="warning" key={member.id}>
              <strong>{member.firstName} {member.lastName}</strong> - 
              Kalan süre: {member.remainingDays} gün
              (Bitiş: {member.membershipEnd})
            </Alert>
          ))}
        </Section>
      )}

      {lowCreditMembers.length > 0 && (
        <Section>
          <SectionTitle>⚠️ Rezervasyon Hakkı Azalan Üyeler</SectionTitle>
          {lowCreditMembers.map(member => (
            <Alert type="danger" key={member.id}>
              <strong>{member.firstName} {member.lastName}</strong> - 
              Kalan rezervasyon hakkı: {member.remainingReservations}
            </Alert>
          ))}
        </Section>
      )}
    </Container>
  );
};

